import { ListItemAvatar, ListItemText, MenuItem } from '@mui/material'
import { WorkingStepIconSvg } from 'components/Common/WorkingSteps/WorkingStepIcon'
import { saveBomItemIsPurchasingPart } from 'features/BillOfMaterials/store/asyncActions/saveBomItemIsPurchasingPart'
import { projectSelectors } from 'features/BillOfMaterials/store/selectors/projectSelectors'
import { useBomItemDetailsDialog } from 'features/BomItemDetailsDialog/useBomItemDetailsDialog'
import { isEqual } from 'lodash'
import { BomItemPointer } from 'model/Project/BomItemPointer'
import { useTranslation } from 'react-i18next'
import { WorkingStepType } from 'services/APIs/InternalAPI/internal-api.contracts'
import { useAppDispatch, useAppSelector } from 'store/configureStore'

export const PurchasingPartMenuItem = ({
  bomItemPointer,
  onClose,
  dataTestId,
}: {
  bomItemPointer?: BomItemPointer
  onClose?: () => void
  dataTestId?: string
}) => {
  const selectedBomItemsIds = useAppSelector(
    projectSelectors.selectedBomItemsPointersSelector,
    isEqual,
  )

  const isPurchasingPart = useAppSelector((state) => {
    if (selectedBomItemsIds?.length > 1) {
      return projectSelectors.bomItemIsPurchasingPartSelector(
        state.project?.selectedBomItemPointers[0],
      )(state)
    } else {
      return projectSelectors.bomItemIsPurchasingPartSelector(bomItemPointer)(
        state,
      )
    }
  })

  const dispatch = useAppDispatch()

  const { t } = useTranslation()

  const openDetailsDialog = useBomItemDetailsDialog()

  const isBuyingParty = useAppSelector(
    projectSelectors.projectInBuyerViewSelector,
  )

  const isEditable = useAppSelector(
    projectSelectors.ProjectEditableStateSelector,
  )

  if (isBuyingParty || !isEditable) {
    return null
  }

  const openPurchasingPartDialog = () => {
    onClose && onClose()

    if (isPurchasingPart) {
      dispatch(saveBomItemIsPurchasingPart({ bomItemPointer: bomItemPointer }))
    } else {
      dispatch(saveBomItemIsPurchasingPart({ bomItemPointer: bomItemPointer }))
      openDetailsDialog('articles', {
        bomItemPointer,
        workingStep: WorkingStepType.Purchasing,
      })
    }
  }

  const handleTogglePurchasingPart = () => {
    dispatch(
      saveBomItemIsPurchasingPart({
        bomItemPointer: selectedBomItemsIds?.length > 1 ? null : bomItemPointer,
      }),
    )
    onClose && onClose()
  }

  const handleClick = () => {
    if (selectedBomItemsIds?.length > 1) {
      handleTogglePurchasingPart()
    } else {
      openPurchasingPartDialog()
    }
  }

  const PrimaryComponent = () => {
    if (selectedBomItemsIds?.length > 1) {
      return (
        <>
          {t(
            'project:purchasing-part-set-unset',
            'set or unset as purchasing part',
          )}
        </>
      )
    }

    if (isPurchasingPart) {
      return (
        <>{t('project:purchasing-part-unset', 'unset as purchasing part')}</>
      )
    } else {
      return <>{t('project:purchasing-part-set', 'set as purchasing part')}</>
    }
  }

  return (
    <MenuItem onClick={handleClick} data-testid={dataTestId}>
      <ListItemAvatar>
        <WorkingStepIconSvg
          workingStepType={WorkingStepType.Purchasing}
          attributes={{
            width: '2rem',
          }}
        />
      </ListItemAvatar>
      <ListItemText
        primary={<PrimaryComponent />}
        secondary={t(
          'project:purchasing-part-set-desc',
          'manual set price/batch',
        )}
      />
    </MenuItem>
  )
}
